type AuthErrorContext = 'login' | 'signup' | 'passwordRecovery'

type TranslateFn = (key: string, defaultValue: string) => string

const extractErrorCode = (error: unknown): string => {
    if (!error || typeof error !== 'object') {
        return ''
    }

    const err = error as { code?: string, error?: { code?: string } }
    return err.code || err.error?.code || ''
}

const extractErrorMessage = (error: unknown): string => {
    if (!error || typeof error !== 'object') {
        return ''
    }

    const err = error as { message?: string, error?: { message?: string } }
    return String(err.message || err.error?.message || '').toLowerCase()
}

const isNetworkError = (errorCode: string, errorMessage: string): boolean => {
    if (errorCode === 'auth/network-request-failed') {
        return true
    }

    return (
        errorMessage.includes('network')
        || errorMessage.includes('failed to fetch')
        || errorMessage.includes('fetch failed')
        || errorMessage.includes('networkerror')
        || errorMessage.includes('offline')
    )
}

export const getAuthErrorMessage = (
    error: unknown,
    t: TranslateFn,
    context: AuthErrorContext = 'login',
): string => {
    const errorCode = extractErrorCode(error)
    const errorMessage = extractErrorMessage(error)

    if (isNetworkError(errorCode, errorMessage)) {
        return t('auth.errors.networkError', 'Error de conexión. Verifica tu internet.')
    }

    if (errorCode === 'auth/invalid-email' || errorMessage.includes('invalid-email')) {
        return t('auth.errors.invalidEmail', 'Email inválido')
    }

    if (context === 'login') {
        if (
            errorCode === 'auth/wrong-password'
            || errorCode === 'auth/invalid-credential'
            || errorMessage.includes('wrong-password')
            || errorMessage.includes('invalid-credential')
        ) {
            return t('auth.errors.wrongPassword', 'Contraseña incorrecta')
        }

        if (errorCode === 'auth/user-not-found' || errorMessage.includes('user-not-found')) {
            return t('auth.errors.userNotFound', 'No se encontró ninguna cuenta con ese email')
        }
    }

    if (context === 'signup') {
        if (errorCode === 'auth/email-already-in-use' || errorMessage.includes('email-already-in-use')) {
            return t('auth.errors.emailInUse', 'Ya existe una cuenta con ese email')
        }

        if (errorCode === 'auth/weak-password' || errorMessage.includes('weak-password')) {
            return t('auth.errors.weakPassword', 'La contraseña debe tener al menos 8 caracteres')
        }
    }

    if (errorCode === 'auth/too-many-requests' || errorMessage.includes('too-many-requests')) {
        return t('auth.errors.tooManyRequests', 'Demasiados intentos. Por favor intenta más tarde.')
    }

    return t('auth.errors.unknown', 'Ocurrió un error. Por favor intenta de nuevo.')
}
